import React, { Component } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { NavMenu } from './NavMenu';
import { OmimLinks, GeneLink, ClinGenLink, OMIM_URL, GENE_URL, CLINGEN_URL, omimNumbers, referenceHref } from './diseaseLinks';
import { asSentence, visibleText } from '../sentence';
import { trackEvent, TARGET_RESOURCE } from '../analytics';

// Function wrapper: React Router 6 exposes the route param only via hooks,
// which a class cannot call. It reads :key and passes it (plus navigate) down.
export function DiseaseDetail() {
  const { key } = useParams();
  const navigate = useNavigate();
  return <DiseaseDetailView entryKey={key} navigate={navigate} />;
}

// One labelled block of the page. The facts strip, the field groups and the
// reference list all use it, so the heading level and wrapper live in one
// place and the heading outline covers every block. `hideLabel` keeps the
// heading for assistive tech and the outline but off the screen.
function Section({ label, hideLabel, children }) {
  return <section><h2 className={hideLabel ? 'visually-hidden' : undefined}>{label}</h2>{children}</section>;
}

class DiseaseDetailView extends Component {
  constructor(props) {
    super(props);
    this.state = { status: 'loading', data: null };
    this._abort = null;
    this._unmounted = false;
  }

  componentDidMount() { this.load(); }

  // Refetch on a detail->detail param change, BUT skip when the param merely
  // caught up to the canonical we already loaded (avoids a spinner-flicker
  // double-fetch after the canonical-correction navigate below).
  componentDidUpdate(prevProps) {
    if (prevProps.entryKey !== this.props.entryKey
        && this.state.data?.canonicalKey !== this.props.entryKey) {
      this.load();
    }
  }

  componentWillUnmount() {
    this._unmounted = true;
    this._abort?.abort();
  }

  async load() {
    const requestedKey = this.props.entryKey;      // snapshot; props may change mid-await
    this._abort?.abort();                          // cancel any prior in-flight load
    const controller = new AbortController();
    this._abort = controller;
    // jsdom 16 (the test env) has no AbortSignal.timeout, so implement the
    // 20s cap with a timer that aborts the controller.
    const timer = setTimeout(() => controller.abort(), 20000);
    this.setState({ status: 'loading', data: null });
    try {
      const response = await fetch(
        '/api/diseases/bysymbol/' + encodeURIComponent(requestedKey),
        { signal: controller.signal });
      if (requestedKey !== this.props.entryKey || this._unmounted) return;  // superseded/unmounted
      if (response.status === 404) { this.setState({ status: 'notfound' }); return; }
      if (!response.ok) { this.setState({ status: 'error' }); return; }
      const data = await response.json();
      if (requestedKey !== this.props.entryKey || this._unmounted) return;  // superseded during json()
      this.setState({ status: 'ok', data });
      // Address-bar tidy-up only (not a crawler signal). Compare against the
      // key THIS load requested, never live props. Router navigate + replace
      // so Back is unaffected.
      if (data.canonicalKey && data.canonicalKey !== requestedKey) {
        this.props.navigate('/disease/' + encodeURIComponent(data.canonicalKey), { replace: true });
      }
    } catch (err) {
      if (requestedKey === this.props.entryKey && !this._unmounted) {
        console.error('bysymbol load failed for', requestedKey, err);
        this.setState({ status: 'error' });
      }
    } finally {
      clearTimeout(timer);
    }
  }

  fireOutbound = (targetResource) => {
    trackEvent('reference_outbound', {
      target_resource: targetResource,
      gene_symbol: this.state.data?.canonicalKey,
    });
  };

  render() {
    const { status, data } = this.state;

    if (status === 'loading') {
      return (<div><NavMenu /><div className="loader section-center"></div></div>);
    }

    if (status === 'notfound') {
      return (
        <div>
          <Helmet><meta name="robots" content="noindex" /><title>Not found — RetNet</title></Helmet>
          <NavMenu />
          <div className="section-center">
            <p>Gene or entry "{this.props.entryKey}" was not found. See the full <a href="/disease">disease table</a>.</p>
          </div>
        </div>
      );
    }

    if (status === 'error') {
      return (
        <div>
          <Helmet><meta name="robots" content="noindex" /><title>Error — RetNet</title></Helmet>
          <NavMenu />
          <div className="section-center">
            <p>Something went wrong loading this entry.</p>
            <button type="button" onClick={() => this.load()}>Retry</button>
          </div>
        </div>
      );
    }

    const e = data.entry;
    const omim = omimNumbers(e.mcKusick);
    const aliases = (data.aliases || []).filter(a => a !== data.canonicalKey);
    const hasExternal = Boolean(e.locusLink || e.clinGen);
    return (
      <div>
        <Helmet>
          <title>{data.metaTitle}</title>
          {data.metaDescription ? <meta name="description" content={data.metaDescription} /> : null}
          <link rel="canonical" href={data.canonicalUrl} />
          <meta property="og:type" content="article" />
          <meta property="og:title" content={data.metaTitle} />
          {data.metaDescription ? <meta property="og:description" content={data.metaDescription} /> : null}
          <meta property="og:url" content={data.canonicalUrl} />
          <meta property="og:site_name" content="RetNet" />
          <meta name="twitter:card" content="summary" />
          <meta name="twitter:title" content={data.metaTitle} />
          {data.metaDescription ? <meta name="twitter:description" content={data.metaDescription} /> : null}
          {this.renderJsonLd(e, data)}
        </Helmet>
        <NavMenu />
        <article className="gene-detail">
          <h1>{e.symbol1 || data.canonicalKey}</h1>
          {aliases.length
            ? <p className="gene-aliases"><span>Also known as</span> {aliases.join(', ')}</p>
            : null}
          {(e.location || omim.length || hasExternal)
            ? <Section label="Key facts" hideLabel><dl className="gene-facts">
                {e.location ? <div><dt>Location</dt><dd dangerouslySetInnerHTML={{ __html: e.location }} /></div> : null}
                {omim.length
                  ? <div><dt>{omim.length > 1 ? 'OMIM numbers' : 'OMIM number'}</dt>
                      <dd><OmimLinks mckusick={e.mcKusick} onOutbound={() => this.fireOutbound(TARGET_RESOURCE.OMIM)} /></dd></div>
                  : null}
                {hasExternal
                  ? <div><dt>External links</dt>
                      <dd className="gene-external">
                        <GeneLink locusLink={e.locusLink} label="NCBI Gene" onOutbound={() => this.fireOutbound(TARGET_RESOURCE.NCBI_GENE)} />
                        <ClinGenLink clinGen={e.clinGen} label="ClinGen" onOutbound={() => this.fireOutbound(TARGET_RESOURCE.CLINGEN)} />
                      </dd></div>
                  : null}
              </dl></Section>
            : null}
          <div className="gene-sections">
            {this.renderSection({ one: 'Disease', many: 'Diseases' }, [e.disease1, e.disease2])}
            {this.renderSection('Protein', [e.protein])}
            {this.renderSection('How identified', [e.mappingMethod])}
            {this.renderSection('Comments', [e.comment1, e.comment2, e.comment3])}
            {e.references?.length
              ? <Section label="References">
                  {/* Explicit role: WebKit/VoiceOver drops list semantics once list-style is none,
                      so this is not redundant there, whatever jsx-a11y assumes. */}
                  {/* eslint-disable-next-line jsx-a11y/no-redundant-roles */}
                  <ul className="gene-references" role="list">
                    {e.references.map((ref, i) => (
                      <li key={i}>
                        {ref.referenceId == null
                          ? ref.reference
                          : <a href={referenceHref(ref.referenceId)}>{ref.reference}</a>}
                      </li>
                    ))}
                  </ul>
                </Section>
              : null}
          </div>
          {data.chromTag
            ? <p className="gene-back">[ <a href={'/disease#' + data.chromTag}>{data.chromLabel || 'Back to table'}</a> | <a href="/disease">Full table</a> ]</p>
            : <p className="gene-back">[ <a href="/disease">Full table</a> ]</p>}
        </article>
      </div>
    );
  }

  // One labelled block per field group, omitted entirely when no field has
  // visible text. `label` is a string, or { one, many } chosen by how many
  // things are listed: populated fields, plus ";"-separated entries within
  // a field (MERTK lists two diseases in disease1 alone). Each field is its
  // own item (no "; " run-on), presented as a sentence. Fields are
  // curator-authored HTML, rendered as the table does.
  renderSection(label, fields) {
    const items = fields.map(asSentence).filter(Boolean);
    if (!items.length) return null;
    const listed = items.reduce((n, html) => n + visibleText(html).split(';').length, 0);
    const text = typeof label === 'string' ? label : label[listed > 1 ? 'many' : 'one'];
    return (
      <Section label={text}>
        <div className="gene-items">
          {items.map((html, i) => <div key={i} dangerouslySetInnerHTML={{ __html: html }} />)}
        </div>
      </Section>
    );
  }

  renderJsonLd(e, data) {
    const sameAs = [];
    omimNumbers(e.mcKusick).forEach(t => sameAs.push(OMIM_URL + t.replace(/ /g, '')));
    if (e.locusLink) sameAs.push(GENE_URL + e.locusLink);
    if (e.clinGen) sameAs.push(CLINGEN_URL + e.clinGen);
    // Origin from server-provided canonicalUrl (no hardcoded host); strip the
    // /disease/<key> tail to get the site root.
    const origin = data.canonicalUrl.replace(/\/disease\/.*$/, '');
    const graph = [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: origin + '/' },
          { '@type': 'ListItem', position: 2, name: 'Disease Genes and Loci', item: origin + '/disease' },
          { '@type': 'ListItem', position: 3, name: data.canonicalKey, item: data.canonicalUrl },
        ],
      },
      {
        '@type': 'MedicalCondition',
        name: data.canonicalKey,
        alternateName: data.aliases || [],   // server-split; no client re-tokenising of symbol2
        url: data.canonicalUrl,
        ...(sameAs.length ? { sameAs } : {}),
      },
    ];
    const ld = { '@context': 'https://schema.org', '@graph': graph };
    // Escape the JSON so curator-authored fields (aliases, clinGen, locusLink)
    // cannot break out of the <script> context: </script>, <!--, U+2028/9.
    const safe = JSON.stringify(ld)
      .replace(/</g, '\\u003c').replace(/>/g, '\\u003e').replace(/&/g, '\\u0026')
      .replace(/\u2028/g, '\\u2028').replace(/\u2029/g, '\\u2029');
    return <script type="application/ld+json">{safe}</script>;
  }
}
